import type { GetServerSidePropsResult, NextPage } from 'next';
import { SWRConfig } from 'swr';
import type { Units } from '@surfline/web-common';

import { getUser } from 'common/api/user';
import { DEFAULT_UNITS } from 'common/constants';
import Favorites from 'containers/Favorites';
import type { WavetrakServerSidePropsContext } from 'pages';
import type { AppPageProps } from 'pages/_app';
import { PageContextProvider } from 'contexts/PageContext';
import type { UserFavoritesResponse } from 'types/userFavorites';
import { getPopularSpots } from 'utils/favoritesFetch';
import { userToFavouritesResponseTransformer } from 'utils/transformers';
import { getUserCountryCode } from 'utils/user';

interface FavouritesPageProps extends AppPageProps {
  fallback: { [x: string]: unknown };
  isAnonUser: boolean;
  entitlements: string[];
  units: Units;
}

const FavoritesPage: NextPage<FavouritesPageProps> = ({ fallback, units }) => (
  <SWRConfig value={{ fallback }}>
    <PageContextProvider pageName="Favorites">
      <Favorites surfHeightUnit={units?.surfHeight} />
    </PageContextProvider>
  </SWRConfig>
);

export const getServerSideProps = async (
  context: WavetrakServerSidePropsContext,
): Promise<GetServerSidePropsResult<FavouritesPageProps>> => {
  const isServer = true;
  const user = await getUser(
    {
      ...context,
      pathname: context.resolvedUrl,
    },
    isServer,
  );
  const userCountryCode = getUserCountryCode(user);

  const favoritesFallback: UserFavoritesResponse = userToFavouritesResponseTransformer(user);

  // skip fetching from /spots/popular (aka favorites fallback) endpoint if user is not anonymous
  const spotsFallback = !user?.details
    ? { '/kbyg/spots/popular?': await getPopularSpots(userCountryCode) }
    : {};

  return {
    props: {
      units: (user?.settings?.units ?? DEFAULT_UNITS) as Units,
      fallback: {
        '/kbyg/favorites?': favoritesFallback,
        ...spotsFallback,
      },
      enableTheme: true,
      enableCssBaseline: true,
      entitlements: user?.entitlements ?? [],
      isAnonUser: !user?.details,
    },
  };
};

export default FavoritesPage;
